import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Mascot from "@/components/Mascot";
import {
  LayoutDashboard, Users, BookOpen, ClipboardList, CalendarCheck,
  Sparkles, FileBarChart, Bell, LogOut, Camera, GraduationCap, Star, Menu, X, KeyRound, Download, Gamepad2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { usePwaInstall } from "@/lib/pwa";
import { flushQueue, readQueue, readGradeQueue } from "@/lib/offlineQueue";
import { toast } from "sonner";

const GURU_NAV = [
  { to: "/guru", icon: LayoutDashboard, label: "Beranda", end: true, id: "dashboard" },
  { to: "/guru/murid", icon: Users, label: "Murid", id: "students" },
  { to: "/guru/mapel", icon: BookOpen, label: "Mata Pelajaran", id: "subjects" },
  { to: "/guru/tugas", icon: ClipboardList, label: "Tugas", id: "assignments" },
  { to: "/guru/absensi", icon: CalendarCheck, label: "Absensi", id: "attendance" },
  { to: "/guru/review", icon: Sparkles, label: "Review AI", id: "review" },
  { to: "/guru/permainan", icon: Gamepad2, label: "Kuis Langsung", id: "games" },
  { to: "/guru/laporan", icon: FileBarChart, label: "Laporan", id: "reports" },
];

const MURID_NAV = [
  { to: "/murid", icon: LayoutDashboard, label: "Beranda", end: true, id: "dashboard" },
  { to: "/murid/absensi", icon: Camera, label: "Absensi", id: "attendance" },
  { to: "/murid/tugas", icon: ClipboardList, label: "Tugas", id: "assignments" },
  { to: "/murid/main", icon: Gamepad2, label: "Main Kuis", id: "games" },
  { to: "/murid/nilai", icon: Star, label: "Nilai & Bintang", id: "grades" },
];

function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const unread = items.filter((i) => !i.read).length;

  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const markAll = async () => {
    await api.put("/notifications/read-all");
    load();
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} className="relative p-2.5 rounded-xl hover:bg-slate-100 transition-colors" data-testid="notification-bell-btn">
        <Bell className="w-5 h-5 text-slate-700" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notification-count">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white rounded-2xl shadow-xl border border-slate-200 z-40 overflow-hidden animate-pop" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <span className="font-display font-semibold">Notifikasi</span>
              <button onClick={markAll} className="text-xs text-teal-600 font-medium" data-testid="notification-mark-all">Tandai dibaca</button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 && <p className="p-4 text-sm text-slate-500">Belum ada notifikasi</p>}
              {items.map((n) => (
                <div key={n.id} className={`px-4 py-3 border-b border-slate-50 text-sm ${n.type === "semangat" ? (n.read ? "text-emerald-700/70" : "text-emerald-800 bg-emerald-50") : n.read ? "text-slate-500" : "text-slate-800 bg-teal-50/40"}`} data-testid={`notif-${n.type}`}>
                  <p>{n.type === "semangat" && <Sparkles className="inline w-3.5 h-3.5 mr-1 -mt-0.5 text-emerald-600" />}{n.message}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const { canInstall, install } = usePwaInstall();
  const nav = user?.role === "guru" ? GURU_NAV : MURID_NAV;

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/badges/counts").then((r) => setCounts(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (user?.role !== "guru") return;
    const syncAll = async () => {
      if (!readQueue().length && !readGradeQueue().length) return;
      const r = await flushQueue();
      if (r.sent) toast.success(`${r.sent} absensi offline berhasil dikirim`);
      if (r.grades) toast.success(`${r.grades} nilai offline berhasil dikirim`);
    };
    syncAll();
    window.addEventListener("online", syncAll);
    return () => window.removeEventListener("online", syncAll);
  }, [user?.role]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const SideContent = () => (
    <>
      <div className="flex items-center gap-3 px-5 py-6">
        <Mascot size={44} className="animate-float" />
        <div>
          <p className="font-display font-bold text-lg leading-none text-slate-800">DUNIA KELAS</p>
          <p className="text-xs text-teal-600 font-medium">AI untuk SD</p>
        </div>
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {nav.map((item) => {
          const badge = item.id === "assignments" ? (counts.assignments || 0) : 0;
          return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${item.id}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-teal-600 text-white shadow-sm" : "text-slate-600 hover:bg-teal-50 hover:text-teal-700"
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="flex-1">{item.label}</span>
            {badge > 0 && (
              <span className="ml-auto bg-rose-500 text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 animate-pulse" data-testid={`nav-badge-${item.id}`}>
                {badge}
              </span>
            )}
          </NavLink>
          );
        })}
      </nav>
      <div className="p-3 space-y-1">
        {canInstall && (
          <button onClick={() => { setMobileOpen(false); install(); }} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 w-full transition-colors" data-testid="sidebar-install-btn">
            <Download className="w-5 h-5" /> Instal Aplikasi
          </button>
        )}
        <button onClick={() => { setMobileOpen(false); setPwOpen(true); }} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-600 hover:bg-teal-50 hover:text-teal-700 w-full transition-colors" data-testid="change-password-btn">
          <KeyRound className="w-5 h-5" /> {user?.role === "murid" ? "Pengaturan Akun" : "Ganti Kata Sandi"}
        </button>
        <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 w-full transition-colors" data-testid="logout-btn">
          <LogOut className="w-5 h-5" /> Keluar
        </button>
      </div>
    </>
  );

  return (
    <div className="grain min-h-screen flex bg-[#FAF9F5]">
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      {/* desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-white border-r border-slate-200 fixed inset-y-0 z-20">
        <SideContent />
      </aside>

      {/* mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 flex flex-col bg-white animate-pop">
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 p-1"><X className="w-5 h-5" /></button>
            <SideContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-10 backdrop-blur-md bg-white/90 border-b border-slate-200/80">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3">
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 rounded-xl hover:bg-slate-100"><Menu className="w-5 h-5" /></button>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${user?.role === "guru" ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"}`}>
                  {user?.role === "guru" ? <GraduationCap className="w-3.5 h-3.5" /> : <Star className="w-3.5 h-3.5" />}
                  {user?.role === "guru" ? (user?.teacher_type === "wali" && user?.wali_kelas ? `Wali Kelas ${user.wali_kelas}${user.wali_rombel || ""}` : "Guru Mapel") : `Murid · Kelas ${user?.kelas}${user?.rombel || ""}`}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <NotificationBell />
              <div className="flex items-center gap-2 pl-1">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center font-display font-semibold text-sm">
                  {user?.name?.[0]?.toUpperCase()}
                </div>
                <span className="hidden sm:block text-sm font-medium text-slate-700" data-testid="current-user-name">{user?.name}</span>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 relative z-[2]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
